// ONE branch boot for both pipelines: construct, two-phase device init, ready lines.
// Notes: ~/comments/aos-threejs-poc/bootBranches_bootBranches_order_is_load_bearing.md

import * as THREE from "three";
import { OGSBranch } from "../graph/nodes/OGSBranch.ts";
import { branchPortNames } from "../graph/branchPorts.ts";
import { prepareOgsLiftDevice, resolveOgsLiftDevice } from "../inference/gpuDevice.ts";
import type { MultiRegionScene } from "../assets/loadMultiRegionScene.ts";
import type { OgsGpuFlags } from "../inference/ogsGpuFlags.ts";
import type { NodeContext } from "../graph/types.ts";
import type { RigResolution } from "../inference/rigMode.ts";
import type { OgsLogger } from "./ogsLog.ts";
import { prefersFp16, type OgsPrecision } from "./precision.ts";

/** What the loader hands over — the bundle's bytes and the rig it resolved. */
export interface BundlePacks {
  getBytes(name: string): Uint8Array | undefined;
  fetchBytes(name: string): Promise<Uint8Array>;
  rigNames?: string[] | null;
  rigPlan?: RigResolution;
}

export interface BootBranchesInput {
  /** The `onnxruntime-web` namespace, injected — the branches build their sessions from it. */
  ort: NodeContext["ort"];
  providers: string[];
  flags: OgsGpuFlags;
  scene: MultiRegionScene;
  packs: BundlePacks;
  precision: OgsPrecision;
  /** `shared` GpuLifter or the myra fork — the caller decides from the subject. */
  lift: "shared" | "myra";
  /** Packed words (one present) or SoA (the drive culls through the fields). */
  packed: boolean;
  worldScale: number;
  worldOffset: readonly [number, number, number];
  worldRotation: THREE.Quaternion | null;
  onDowngrade?: (kind: string, reason: string) => void;
  /** Called with the branches BEFORE any session exists — see the note. */
  onBranches?: (branches: OGSBranch[]) => void;
  isCancelled: () => boolean;
  log: OgsLogger;
}

export interface BootedBranches {
  branches: OGSBranch[];
  /** The device the lift runs on — the page's one device, or null with no WebGPU.
   *  Was `sharedDevice`, which went null whenever the decoder IO was off. */
  device: GPUDevice | null;
  ioMode: "gpu-buffer" | "cpu";
  demoted: boolean;
  lines: string[];
  /** The context the terminal render node is initialised with — the branches' own. */
  rendererCtx: NodeContext;
}

/** Build every branch of a scene and bring it up. Null when cancelled mid-boot. */
export async function bootBranches(input: BootBranchesInput): Promise<BootedBranches | null> {
  const { ort, providers, flags, scene, packs, precision, lift: liftImpl, packed, log } = input;
  const cancelled = () => input.isCancelled();

  // ORDER IS LOAD-BEARING (gpuDevice.ts): prepare BEFORE the first session create,
  // resolve AFTER — ORT caches its device on that first create.
  const prepared = await prepareOgsLiftDevice(ort, providers, flags);
  if (cancelled()) return null;

  const branches = scene.branches.map((assets, index) => new OGSBranch({
    id: `branch_${assets.name}`,
    name: assets.name,
    members: [`branch_${assets.name}.geom`, `branch_${assets.name}.appr`],
    ports: [...branchPortNames(assets.name, index)],
    live: {
      assets,
      providers,
      getBytes: packs.getBytes,
      fetchBytes: packs.fetchBytes,
      preferFp16: prefersFp16(precision),
      originScale: 1 / input.worldScale,
      worldOffset: input.worldOffset,
      worldRotation: input.worldRotation,
      onDowngrade: input.onDowngrade,
      pbr: scene.manifest.pbr,
      flags,
      lift: liftImpl,
      rigNames: packs.rigNames ?? undefined,
      rigPlan: packs.rigPlan,
    },
  }));
  // Published before the first session: teardown can only release what it can see.
  input.onBranches?.(branches);

  const ctxFor = (branch: OGSBranch, device: GPUDevice | null): NodeContext => ({
    ort, uvRes: branch.uvRes, rigDim: branch.rigDim, device, gpuBufferIo: prepared.shared,
  });

  for (const branch of branches) {
    await branch.init(ctxFor(branch, null));
    if (cancelled()) return null;
  }

  const lift = await resolveOgsLiftDevice(ort, prepared);
  if (cancelled()) return null;
  // ORT built a device the lifter cannot share: the gpu-buffer hints are baked in
  // at create time, so the only repair is to download every output. Loud, never silent.
  if (lift.demoted) for (const branch of branches) branch.setDownloadOutputs(true);
  const device = lift.device;
  const ioMode = prepared.shared && !lift.demoted ? "gpu-buffer" : "cpu";

  for (const branch of branches) {
    await branch.initGpu(ctxFor(branch, device), lift.device, { packed });
    if (cancelled()) return null;
  }

  const lines = branches.map((branch) => `branch '${branch.name}' ready: `
    + `V=${branch.neutralVertices.length / 3} uv=${branch.uvRes} rigDim=${branch.rigDim} `
    + `deform=${branch.deformKind} lift=${branch.liftKind()} plucker=${branch.pluckerKind()} `
    + `io=${ioMode} geom=${branch.geomUsedFp16 ? "fp16" : "fp32"} appr=${branch.apprUsedFp16 ? "fp16" : "fp32"}`);
  for (const line of lines) log.info(line);

  return {
    branches, device, ioMode, demoted: lift.demoted, lines,
    rendererCtx: ctxFor(branches[0], device),
  };
}

/** Every branch's neutral vertices concatenated — what the camera is framed to at boot. */
export function neutralPositions(branches: readonly OGSBranch[]): Float32Array {
  const total = branches.reduce((sum, branch) => sum + branch.neutralVertices.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const branch of branches) { out.set(branch.neutralVertices, offset); offset += branch.neutralVertices.length; }
  return out;
}
